import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Checkbox,
    Chip,
    CircularProgress,
    Link,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tooltip,
    Typography
} from "@mui/material";
import { ExpandMore, Refresh as RefreshIcon } from "@mui/icons-material";
import { TabPanel } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import styled from "@emotion/styled";
import { useDispatch } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { getUnbilledReport } from "../redux/ducks/invoices";
import MoveUnbilledToInvoiceDialog from "./MoveUnbilledToInvoiceDialog";
import { formatDate, formatDayShort } from "../utils/dateUtils";

const CLIENT_PREVIEW_LIMIT = 8;

const ENTITY_LABELS = {
    client: "Client",
    account: "Account",
    group: "Group",
    project: "Project",
    task: "Task"
};

const StyledTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: theme.spacing(3, 0, 0, 0)
}));

const ControlsRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2)
}));

const PickerBox = styled(Box)({
    minWidth: 220
});

const TileRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gap: theme.spacing(2),
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    marginBottom: theme.spacing(2)
}));

const Tile = styled(Box, {
    shouldForwardProp: (prop) => prop !== "$warning"
})(({ theme, $warning }) => ({
    border: `1px solid ${$warning ? theme.palette.warning.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 2,
    background: $warning ? theme.palette.action.hover : theme.palette.background.paper,
    padding: theme.spacing(2)
}));

const TileLabel = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    textTransform: "uppercase",
    letterSpacing: "0.04em"
}));

const TileValue = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    fontSize: theme.typography.h5.fontSize,
    lineHeight: 1.2,
    marginTop: theme.spacing(0.5)
}));

const TileHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    marginTop: theme.spacing(0.5)
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(1)
}));

const SectionHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    marginBottom: theme.spacing(1)
}));

const GroupSummaryRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    width: "100%"
}));

const GroupName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
    minWidth: 140
}));

const GroupFigures = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const ReasonList = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2)
}));

const ReasonRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1)
}));

const ReasonText = styled(Typography)(({ theme }) => ({
    fontSize: theme.typography.size.sm,
    color: theme.palette.text.primary
}));

const TableScroll = styled(Box)({
    overflowX: "auto"
});

const NumberCell = styled(TableCell)({
    textAlign: "right",
    whiteSpace: "nowrap"
});

const MutedNumberCell = styled(TableCell)(({ theme }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary
}));

const DeletedNumberCell = styled(TableCell)(({ theme }) => ({
    textAlign: "right",
    whiteSpace: "nowrap",
    color: theme.palette.warning.dark
}));

const InternalChip = styled(Chip)(({ theme }) => ({
    height: 18,
    marginLeft: theme.spacing(1),
    fontSize: theme.typography.size.xs
}));

const ShowAllButton = styled(Button)(({ theme }) => ({
    textTransform: "none",
    marginTop: theme.spacing(1)
}));

const EmptyState = styled(Box)(({ theme }) => ({
    padding: theme.spacing(8),
    textAlign: "center",
    color: theme.palette.text.secondary
}));

const SelectionBar = styled(Box)(({ theme }) => ({
    position: "sticky",
    bottom: theme.spacing(1),
    zIndex: 2,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius * 2,
    background: theme.palette.background.paper,
    boxShadow: theme.shadows[3]
}));

const SelectionText = styled(Box)({
    flex: "1 1 260px",
    minWidth: 0
});

const SelectionHeadline = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold
}));

const SelectionHint = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const AncestorPanel = styled(Box)(({ theme }) => ({
    border: `1px solid ${theme.palette.warning.main}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75)
}));

const AncestorRow = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    flexWrap: "wrap"
}));

const AncestorName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold
}));

const AncestorFigures = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm
}));

const ResultDetail = styled(Typography)(({ theme }) => ({
    marginTop: theme.spacing(0.5)
}));

const previousMonthStart = () => dayjs().startOf("month").subtract(1, "month");
const currentMonthStart = () => dayjs().startOf("month");
const earliestMonthStart = () => dayjs().startOf("month").subtract(24, "month");

const formatHours = (value) => `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })} h`;

const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });

const formatCount = (value, singular, plural) => `${Number(value || 0).toLocaleString("en-US")} ${Number(value) === 1 ? singular : plural}`;

const positive = (value) => Math.max(0, Number(value) || 0);

const overageHours = (consumption) => (consumption?.overage || []).reduce((sum, line) => sum + (Number(line.hours) || 0), 0);

const outsideCount = (outside) => (Number(outside?.timeRecords) || 0) + (Number(outside?.charges) || 0);

const formatDateRange = (outside) => {
    const earliest = formatDate(outside?.earliest);
    const latest = formatDate(outside?.latest);
    if (!earliest) return "a different period";
    if (!latest || earliest === latest) return formatDayShort(earliest);
    return `${formatDayShort(earliest)} to ${formatDayShort(latest)}`;
};

const normalizeAncestors = (list) => {
    const map = new Map();
    for (const raw of list || []) {
        const entityType = String(raw?.entityType || raw?.type || "").toLowerCase();
        const entityId = raw?.entityId || raw?.id || raw?._id;
        const known = !!ENTITY_LABELS[entityType] && !!entityId;
        const key = known ? `${entityType}:${entityId}` : `record:${raw?.clientId || raw?.clientName || "unknown"}`;
        if (!map.has(key)) {
            map.set(key, {
                key,
                entityType: known ? entityType : null,
                entityId: known ? String(entityId) : null,
                name: raw?.name || "",
                accountRef: raw?.accountSlug || raw?.accountId || null,
                clientName: raw?.clientName || "",
                count: 0,
                hours: 0
            });
        }
        const entry = map.get(key);
        entry.count += Number(raw?.count) || 0;
        entry.hours += Number(raw?.hours) || 0;
    }
    return [...map.values()].sort((a, b) => b.hours - a.hours || b.count - a.count);
};

const ancestorHref = (ancestor) => {
    const account = ancestor.accountRef;
    switch (ancestor.entityType) {
        case "client":
            return `/client/${ancestor.entityId}`;
        case "account":
            return `/account/${account || ancestor.entityId}`;
        case "group":
        case "task":
            return account ? `/account/${account}` : null;
        case "project":
            return account ? `/account/${account}/project/${ancestor.entityId}` : null;
        default:
            return null;
    }
};

const ancestorTooltip = (ancestor, name, href) => {
    if (!ancestor.entityType) return "These time records were deleted themselves — there is no parent to restore. Bring them back where they were logged.";
    if (!href) return `This ${ENTITY_LABELS[ancestor.entityType].toLowerCase()} has no page that can be opened from here. Find it from the account it belongs to.`;
    if (ancestor.entityType === "group") return `Opens the account ${name} belongs to — groups are restored from there`;
    if (ancestor.entityType === "task") return `Opens the account ${name} belongs to — work down to the task to restore it`;
    return `Open ${name}`;
};

const DeletedParents = ({ ancestors }) => {
    if (ancestors.length === 0) return null;

    return (
        <AncestorPanel>
            <SectionHint variant="caption">
                What is holding this work out of billing. Restore a parent and everything under it comes back in a single step — there is nothing to do record by
                record.
            </SectionHint>
            {ancestors.map((ancestor) => {
                const href = ancestorHref(ancestor);
                const label = ancestor.entityType ? ENTITY_LABELS[ancestor.entityType] : "Deleted records";
                const name = ancestor.entityType ? ancestor.name || "Unnamed" : ancestor.clientName || "Unknown client";
                const tooltip = ancestorTooltip(ancestor, name, href);
                return (
                    <AncestorRow key={ancestor.key}>
                        <Chip size="small" color="warning" variant="outlined" label={label} />
                        {href ? (
                            <Tooltip title={tooltip}>
                                <Link component={RouterLink} to={href} underline="hover">
                                    {name}
                                </Link>
                            </Tooltip>
                        ) : (
                            <Tooltip title={tooltip}>
                                <AncestorName variant="body2">{name}</AncestorName>
                            </Tooltip>
                        )}
                        {ancestor.entityType && ancestor.clientName && <AncestorFigures variant="caption">{ancestor.clientName}</AncestorFigures>}
                        <AncestorFigures variant="caption">
                            {formatCount(ancestor.count, "time record", "time records")} &middot; {formatHours(ancestor.hours)}
                        </AncestorFigures>
                    </AncestorRow>
                );
            })}
        </AncestorPanel>
    );
};

const ClientTable = ({ group, valueLabel, formatValue, valueKey, showDeleted, selectedKeys, lockedClientId, lockedClientName, onToggleRow }) => {
    const [showAll, setShowAll] = useState(false);
    const clients = group.clients || [];
    const rows = showAll ? clients : clients.slice(0, CLIENT_PREVIEW_LIMIT);

    return (
        <>
            <TableScroll>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell>Client</TableCell>
                            <NumberCell>Records</NumberCell>
                            <NumberCell>{valueLabel}</NumberCell>
                            {showDeleted && <NumberCell>Hidden by a deleted parent</NumberCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((client) => {
                            const wrongClient = !!lockedClientId && String(lockedClientId) !== String(client.clientId);
                            const blocked =
                                client.liveCount === 0
                                    ? "Every record here is hidden by a deleted parent. Restore the parent instead."
                                    : wrongClient
                                      ? `An invoice carries one client's work only. Clear the ${lockedClientName} selection first.`
                                      : null;

                            return (
                                <TableRow key={client.clientId}>
                                    <TableCell padding="checkbox">
                                        {blocked ? (
                                            <Tooltip title={blocked}>
                                                <span>
                                                    <Checkbox size="small" disabled />
                                                </span>
                                            </Tooltip>
                                        ) : (
                                            <Tooltip title={`Take all ${formatCount(client.liveCount, "record", "records")} for ${client.name} in one go`}>
                                                <Checkbox
                                                    size="small"
                                                    checked={selectedKeys.has(client.rowKey)}
                                                    onChange={() => onToggleRow(group, client)}
                                                    inputProps={{ "aria-label": `Select ${client.name}` }}
                                                />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {client.name}
                                        {client.isInternal && (
                                            <Tooltip title="This is an internal client. Client work logged here never reaches an invoice.">
                                                <InternalChip label="internal" size="small" variant="outlined" />
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                    <MutedNumberCell>{Number(client.count || 0).toLocaleString("en-US")}</MutedNumberCell>
                                    <NumberCell>{formatValue(client[valueKey])}</NumberCell>
                                    {showDeleted && (
                                        <DeletedNumberCell>{client.deletedCount > 0 ? formatHours(client.deletedHours) : "-"}</DeletedNumberCell>
                                    )}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableScroll>
            {clients.length > CLIENT_PREVIEW_LIMIT && (
                <ShowAllButton size="small" onClick={() => setShowAll((prev) => !prev)}>
                    {showAll ? "Show fewer clients" : `Show all ${clients.length} clients`}
                </ShowAllButton>
            )}
        </>
    );
};

const TypeGroup = ({ group, valueLabel, formatValue, valueKey, showDeleted, selectedKeys, lockedClientId, lockedClientName, onToggleRow }) => (
    <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandMore />}>
            <GroupSummaryRow>
                <GroupName variant="body2">{group.billingType}</GroupName>
                <GroupFigures variant="body2">
                    {formatValue(group[valueKey])} across {formatCount(group.count, "record", "records")}
                </GroupFigures>
                {showDeleted && group.deletedCount > 0 && (
                    <Chip
                        size="small"
                        color="warning"
                        variant="outlined"
                        label={`${formatHours(group.deletedHours)} hidden by a deleted parent`}
                    />
                )}
            </GroupSummaryRow>
        </AccordionSummary>
        <AccordionDetails>
            <DeletedParents ancestors={group.ancestors} />
            {group.reasons?.length > 0 && (
                <>
                    <SectionHint variant="caption">Why these did not reach an invoice</SectionHint>
                    <ReasonList>
                        {group.reasons.map((reason) => (
                            <ReasonRow key={reason.reason}>
                                <Chip size="small" label={Number(reason.count).toLocaleString("en-US")} />
                                <ReasonText variant="body2">{reason.reason}</ReasonText>
                            </ReasonRow>
                        ))}
                    </ReasonList>
                </>
            )}
            <ClientTable
                group={group}
                valueLabel={valueLabel}
                formatValue={formatValue}
                valueKey={valueKey}
                showDeleted={showDeleted}
                selectedKeys={selectedKeys}
                lockedClientId={lockedClientId}
                lockedClientName={lockedClientName}
                onToggleRow={onToggleRow}
            />
        </AccordionDetails>
    </Accordion>
);

const BillingUnbilled = ({ tabValue }) => {
    const dispatch = useDispatch();
    const [monthValue, setMonthValue] = useState(previousMonthStart);
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const [selectedRows, setSelectedRows] = useState({});
    const [moveOpen, setMoveOpen] = useState(false);
    const [moveResult, setMoveResult] = useState(null);

    const month = dayjs.isDayjs(monthValue) && monthValue.isValid() ? monthValue.format("YYYY-MM") : null;

    const load = useCallback(() => {
        if (!month) return;
        setLoading(true);
        setFailed(false);
        dispatch(
            getUnbilledReport(month, (data) => {
                setLoading(false);
                if (!data) {
                    setFailed(true);
                    return;
                }
                setReport(data);
            })
        );
    }, [dispatch, month]);

    useEffect(() => {
        if (tabValue !== "unbilled") return;
        load();
    }, [tabValue, load]);

    useEffect(() => {
        setSelectedRows({});
    }, [report]);

    const groups = useMemo(() => {
        const decorate = (list, kind) =>
            (list || []).map((group) => ({
                ...group,
                kind,
                ancestors: normalizeAncestors(group?.deletedAncestors),
                clients: (group.clients || []).map((client) => ({
                    ...client,
                    rowKey: `${kind}|${group.billingType}|${client.clientId}`,
                    liveCount: positive((Number(client.count) || 0) - (Number(client.deletedCount) || 0)),
                    liveHours: positive((Number(client.hours) || 0) - (Number(client.deletedHours) || 0)),
                    liveAmount: Number(client.amount) || 0
                }))
            }));

        return {
            time: decorate(report?.time?.byBillingType, "time"),
            charges: decorate(report?.charges?.byBillingType, "charges")
        };
    }, [report]);

    const orphanedAncestors = useMemo(() => {
        if (groups.time.some((group) => group.ancestors.length > 0)) return [];
        return normalizeAncestors(report?.time?.deletedAncestors);
    }, [groups, report]);

    const selection = useMemo(() => {
        const rows = Object.values(selectedRows);
        const timeRows = rows.filter((row) => row.kind === "time");
        const chargeRows = rows.filter((row) => row.kind === "charges");
        return {
            clientId: rows[0]?.clientId || null,
            clientName: rows[0]?.clientName || "",
            timeBillingTypes: timeRows.map((row) => row.billingType),
            chargeBillingTypes: chargeRows.map((row) => row.billingType),
            reportedTimeCount: timeRows.reduce((sum, row) => sum + row.count, 0),
            reportedHours: timeRows.reduce((sum, row) => sum + row.hours, 0),
            reportedChargeCount: chargeRows.reduce((sum, row) => sum + row.count, 0),
            reportedAmount: chargeRows.reduce((sum, row) => sum + row.amount, 0)
        };
    }, [selectedRows]);

    const selectedKeys = useMemo(() => new Set(Object.keys(selectedRows)), [selectedRows]);
    const selectedCount = selection.reportedTimeCount + selection.reportedChargeCount;

    const toggleRow = useCallback((group, client) => {
        setSelectedRows((prev) => {
            const next = { ...prev };
            if (next[client.rowKey]) {
                delete next[client.rowKey];
                return next;
            }
            next[client.rowKey] = {
                rowKey: client.rowKey,
                kind: group.kind,
                billingType: group.billingType,
                clientId: client.clientId,
                clientName: client.name,
                count: client.liveCount,
                hours: client.liveHours,
                amount: client.liveAmount
            };
            return next;
        });
    }, []);

    const handleImported = useCallback(
        (outcome) => {
            setMoveOpen(false);
            setSelectedRows({});
            setMoveResult(outcome);
            load();
        },
        [load]
    );

    if (tabValue !== "unbilled") return null;

    const monthLabel = dayjs.isDayjs(monthValue) && monthValue.isValid() ? monthValue.format("MMMM YYYY") : "the selected month";
    const timeGroups = groups.time;
    const chargeGroups = groups.charges;
    const deletedCount = report?.time?.deletedCount || 0;
    const hasAnything = (report?.time?.count || 0) > 0 || (report?.charges?.count || 0) > 0;

    const selectionProps = {
        selectedKeys,
        lockedClientId: selection.clientId,
        lockedClientName: selection.clientName,
        onToggleRow: toggleRow
    };

    return (
        <StyledTabPanel value="unbilled">
            <Card variant="outlined">
                <CardHeader
                    title={<Typography variant="h6">Unbilled Work</Typography>}
                    subheader="Time and charges dated in the period that landed on no invoice, grouped by the billing type they resolved to"
                />
                <CardContent>
                    <ControlsRow>
                        <PickerBox>
                            <DatePicker
                                label="Month"
                                views={["year", "month"]}
                                openTo="month"
                                format="MMMM YYYY"
                                value={monthValue}
                                minDate={earliestMonthStart()}
                                maxDate={currentMonthStart()}
                                onChange={(value) => setMonthValue(value)}
                                slotProps={{ textField: { size: "small", fullWidth: true } }}
                            />
                        </PickerBox>
                        <Tooltip title="Recheck this month against the invoices that exist right now">
                            <span>
                                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={load} disabled={loading || !month}>
                                    Refresh
                                </Button>
                            </span>
                        </Tooltip>
                    </ControlsRow>

                    {moveResult && (
                        <Alert severity="success" onClose={() => setMoveResult(null)}>
                            {formatCount(moveResult.timeCount + moveResult.chargeCount, "record", "records")} moved onto invoice {moveResult.invoiceNumber}. They
                            have dropped off this report, which is worked out fresh from the records every time it loads.
                            {moveResult.addedOutsidePeriod && (
                                <ResultDetail variant="body2">
                                    {formatCount(outsideCount(moveResult.addedOutsidePeriod), "of them is", "of them are")} dated outside the period that
                                    invoice covers ({formatDateRange(moveResult.addedOutsidePeriod)}), which is expected when the work&apos;s own month has
                                    already been invoiced and sent.
                                </ResultDetail>
                            )}
                            {moveResult.retainerConsumption && (
                                <ResultDetail variant="body2">
                                    {formatHours(moveResult.retainerConsumption.consumedHours)} came out of the {moveResult.retainerConsumption.pool?.name}{" "}
                                    retainer, leaving {formatHours(moveResult.retainerConsumption.remainingHours)} in it.
                                    {overageHours(moveResult.retainerConsumption) > 0 &&
                                        ` ${formatHours(overageHours(moveResult.retainerConsumption))} ran past the pool and has been billed as overage.`}
                                    {moveResult.retainerConsumption.absorbedHours > 0 &&
                                        ` ${formatHours(moveResult.retainerConsumption.absorbedHours)} ran past the pool and was absorbed at no charge,` +
                                            " because this retainer does not bill overage."}
                                </ResultDetail>
                            )}
                        </Alert>
                    )}

                    {loading ? (
                        <EmptyState>
                            <CircularProgress size={24} />
                            <Typography variant="body2">Checking {monthLabel} against every invoice.</Typography>
                        </EmptyState>
                    ) : failed ? (
                        <EmptyState>
                            <Typography variant="body2">The unbilled report could not be loaded. Try again in a moment.</Typography>
                            <ShowAllButton variant="outlined" size="small" onClick={load}>
                                Try again
                            </ShowAllButton>
                        </EmptyState>
                    ) : !report ? (
                        <EmptyState>
                            <Typography variant="body2">Pick a month to see what it left behind.</Typography>
                        </EmptyState>
                    ) : !hasAnything ? (
                        <EmptyState>
                            <Typography variant="body2">Nothing from {monthLabel} was left off an invoice. Every time record and charge in that month is accounted for.</Typography>
                        </EmptyState>
                    ) : (
                        <>
                            <TileRow>
                                <Tile>
                                    <TileLabel variant="caption">Unbilled time</TileLabel>
                                    <TileValue>{formatHours(report.time?.hours)}</TileValue>
                                    <TileHint variant="caption">{formatCount(report.time?.count, "time record", "time records")}</TileHint>
                                </Tile>
                                <Tile>
                                    <TileLabel variant="caption">Unbilled charges</TileLabel>
                                    <TileValue>{formatCurrency(report.charges?.amount)}</TileValue>
                                    <TileHint variant="caption">{formatCount(report.charges?.count, "charge", "charges")}</TileHint>
                                </Tile>
                                {deletedCount > 0 && (
                                    <Tile $warning>
                                        <TileLabel variant="caption">Hidden by a deleted parent</TileLabel>
                                        <TileValue>{formatHours(report.time?.deletedHours)}</TileValue>
                                        <TileHint variant="caption">
                                            {formatCount(deletedCount, "time record", "time records")} sitting under a deleted project, account or client
                                        </TileHint>
                                    </Tile>
                                )}
                            </TileRow>

                            {deletedCount > 0 && (
                                <Alert severity="warning">
                                    Some of this work is hidden rather than merely unbilled. It belongs to a project, account or client that was deleted, so
                                    no invoice can pick it up and moving it onto an invoice will not work either. Restore the parent first, then rerun
                                    billing for {monthLabel}. Each billing type below names the deleted parents holding its work.
                                </Alert>
                            )}

                            {orphanedAncestors.length > 0 && <DeletedParents ancestors={orphanedAncestors} />}

                            <SectionTitle variant="subtitle1">Time by billing type</SectionTitle>
                            <SectionHint variant="body2">
                                The billing type is the one the record resolved to walking up its chain, not the one typed on the record. Client work sitting
                                under an internal client shows up here as Internal, which is the usual sign of a record filed against the wrong client.
                            </SectionHint>
                            <SectionHint variant="body2">
                                Tick a client to take every one of its records in that row at once, then move them onto an invoice that is still open. That is
                                the way to bill work whose own month has already been invoiced and sent.
                            </SectionHint>
                            {timeGroups.length === 0 ? (
                                <SectionHint variant="body2">No unbilled time in {monthLabel}.</SectionHint>
                            ) : (
                                timeGroups.map((group) => (
                                    <TypeGroup
                                        key={group.billingType}
                                        group={group}
                                        valueLabel="Hours"
                                        formatValue={formatHours}
                                        valueKey="hours"
                                        showDeleted
                                        {...selectionProps}
                                    />
                                ))
                            )}

                            <SectionTitle variant="subtitle1">Charges by billing type</SectionTitle>
                            {chargeGroups.length === 0 ? (
                                <SectionHint variant="body2">No unbilled charges in {monthLabel}.</SectionHint>
                            ) : (
                                chargeGroups.map((group) => (
                                    <TypeGroup
                                        key={group.billingType}
                                        group={group}
                                        valueLabel="Amount"
                                        formatValue={formatCurrency}
                                        valueKey="amount"
                                        showDeleted={false}
                                        {...selectionProps}
                                    />
                                ))
                            )}

                            {selectedCount > 0 && (
                                <SelectionBar>
                                    <SelectionText>
                                        <SelectionHeadline variant="body2">
                                            {formatCount(selectedCount, "record", "records")} picked for {selection.clientName}
                                        </SelectionHeadline>
                                        <SelectionHint variant="caption">
                                            {selection.reportedTimeCount > 0 && `${formatHours(selection.reportedHours)} of time`}
                                            {selection.reportedTimeCount > 0 && selection.reportedChargeCount > 0 && " and "}
                                            {selection.reportedChargeCount > 0 && `${formatCurrency(selection.reportedAmount)} of charges`}
                                            {` from ${monthLabel}`}
                                        </SelectionHint>
                                    </SelectionText>
                                    <Button size="small" onClick={() => setSelectedRows({})}>
                                        Clear selection
                                    </Button>
                                    <Button variant="contained" onClick={() => setMoveOpen(true)}>
                                        Move onto an invoice
                                    </Button>
                                </SelectionBar>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <MoveUnbilledToInvoiceDialog
                open={moveOpen && selectedCount > 0}
                onClose={() => setMoveOpen(false)}
                selection={selection}
                month={month}
                monthLabel={monthLabel}
                onImported={handleImported}
            />
        </StyledTabPanel>
    );
};

export default BillingUnbilled;
